import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import Editor from 'ckeditor5-custom-build/build/ckeditor';

const readOnlyLock = 'medicine-form-state';

// Own one editor lifecycle per section. The installed React integration's
// watchdog can destroy an editor twice during rapid language/section changes.
// Wait for creation and release each instance exactly once instead.
export default function MedicineRichTextEditor({ content, config, disabled, onReady, onChange }) {
  const host = useRef(null), current = useRef(null), callbacks = useRef({ onReady, onChange });
  const contentRef = useRef(content), disabledRef = useRef(disabled);
  callbacks.current = { onReady, onChange }; contentRef.current = content; disabledRef.current = disabled;
  const [error, setError] = useState(''), [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let disposed = false, editor, destroyPromise;
    const container = document.createElement('div'), source = document.createElement('div');
    container.appendChild(source); host.current.appendChild(container); setError('');
    function destroy() {
      if (!editor || destroyPromise) return destroyPromise;
      destroyPromise = Promise.resolve(editor.destroy()).finally(() => container.remove());
      return destroyPromise;
    }
    const creation = Editor.create(source, { ...config, initialData: String(contentRef.current || '') }).then(instance => {
      editor = instance;
      if (disposed) return destroy();
      current.current = editor;
      if (disabledRef.current) editor.enableReadOnlyMode(readOnlyLock);
      const latest = String(contentRef.current || '');
      if (editor.getData() !== latest) editor.setData(latest);
      editor.model.document.on('change:data', (event, batch) => {
        if (!disposed && current.current === editor && editor.state === 'ready') callbacks.current.onChange?.(event, editor, batch);
      });
      callbacks.current.onReady?.(editor);
    }).catch(failure => {
      if (!disposed) setError('The content editor could not be loaded. Your medicine text is preserved.');
      container.remove();
      console.error('Medicine editor initialization failed.', failure);
    });
    return () => {
      disposed = true; container.style.display = 'none';
      if (current.current === editor) current.current = null;
      // Creation may still be running after React removes its source container.
      // Its own callback and cleanup share the same destruction promise.
      creation.then(() => destroy()).catch(failure => console.error('Medicine editor cleanup failed.', failure));
    };
  }, [attempt, config]);
  useEffect(() => {
    const editor = current.current;
    if (!editor || editor.state !== 'ready') return;
    disabled ? editor.enableReadOnlyMode(readOnlyLock) : editor.disableReadOnlyMode(readOnlyLock);
  }, [disabled]);
  useEffect(() => {
    const editor = current.current, value = String(content || '');
    if (editor?.state === 'ready' && editor.getData() !== value) editor.setData(value);
  }, [content]);
  return <Box sx={{ minWidth: 0 }}><Box ref={host} />{error && <Alert severity="error" sx={{ mt: 1 }} action={<Button onClick={() => setAttempt(value => value + 1)}>Retry editor</Button>}>{error}</Alert>}</Box>;
}
